import { referralRecord } from "../libs/api-url";
import axios from "@/libs/axios";

/**
 * Referral Record Service
 * Handles all "Referral Records" admin API calls.
 *
 * API Endpoints:
 * - GET /api/v1/admin/referral-record/list - Get referral record list
 * - GET /api/v1/admin/referral-record/countries - Get distinct referrer countries (filter options)
 * - GET /api/v1/admin/referral-record/get - Get a single referral's full detail
 * - GET /api/v1/admin/referral-record/reward-history - Get a referral's referrer's reward history
 * - POST /api/v1/admin/referral-record/mark-fraudulent - Mark a referral as fraudulent
 */
class ReferralRecordService {
  /**
   * @name getList
   * @description Fetches the referral record list from the admin API with pagination, search and filters.
   * @param {Object} params - Query parameters (search, active_status, country, registration_date_from,
   * registration_date_to, page, limit, sort_by, order_by)
   * @returns {Promise<Object>} The response data containing the referral record list.
   */
  static async getList(params = {}) {
    try {
      const response = await axios.get(referralRecord.list, { params });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name getCountries
   * @description Fetches the distinct list of referrer countries, used to populate the Country filter dropdown.
   * @returns {Promise<Object>} The response data containing the country list.
   */
  static async getCountries() {
    try {
      const response = await axios.get(referralRecord.countries);
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name get
   * @description Fetches a single referral's full detail by the referral uuid.
   * @param {string} uuid - Referral UUID (referral_uuid from the list row).
   * @returns {Promise<Object>} The response data containing the referral detail.
   */
  static async get(uuid) {
    try {
      const response = await axios.get(referralRecord.get, { params: { uuid } });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name getRewardHistory
   * @description Fetches the reward-unlock history for the referral's referrer.
   * @param {string} uuid - Referral UUID (same uuid used by `get`).
   * @param {Object} params - Query parameters (page, limit)
   * @returns {Promise<Object>} The response data containing the reward history list.
   */
  static async getRewardHistory(uuid, params = {}) {
    try {
      const response = await axios.get(referralRecord.rewardHistory, { params: { uuid, ...params } });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name markFraudulent
   * @description Marks a referral as fraudulent with a required reason.
   * @param {string} uuid - Referral UUID.
   * @param {string} reason - Reason for flagging the referral as fraudulent.
   * @returns {Promise<Object>} The response data containing the updated referral detail.
   */
  static async markFraudulent(uuid, reason) {
    try {
      const response = await axios.post(referralRecord.markFraudulent, { uuid, reason });
      return response.data;
    } catch (err) {
      throw err;
    }
  }
}

export { ReferralRecordService };
